import { useState, useRef } from "react";
import { auth, googleProvider } from "../../firebase";
import { signInWithPopup, sendEmailVerification } from "firebase/auth";
import GoogleButton from "react-google-button";
import { IoMdAdd } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { UserAuth } from "../../context/AuthContext";

const SignUp = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { createUser } = UserAuth();

  const signUp = async (e: any) => {
    e.preventDefault();
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }
    setLoading(true);
    try {
      const userCredential = await createUser(email, password);

      // Send verification email
      await sendEmailVerification(userCredential.user);

      setError("");
      setSuccess(
        "Account created! Please check your email to verify your account."
      );

      await initializeUser(userCredential.user.uid, userCredential.user.email);

      // Add a delay before showing the success message
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Don't navigate to dashboard until email is verified
      navigate("/signin");
    } catch (error: any) {
      handleError(error.code);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (e: any) => {
    e.preventDefault();
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Google accounts are pre-verified
      await initializeUser(result.user.uid, result.user.email);
      navigate("/dashboard");
    } catch (err) {
      setError("Error signing up with Google");
    }
  };

  const initializeUser = async (uid: string, email: string | null) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/checkout/applydefaultrole`,
        {
          method: "POST",
          body: JSON.stringify({ userId: uid, userEmail: email }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Failed to create user in database: ${
            errorData.error || response.statusText
          }`
        );
      }
    } catch (err) {
      setError("Failed to create user account. Please try again.");
    }
  };

  const handleError = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-credential":
        passwordRef.current?.focus();
        setError("Invalid email/password combination");
        break;
      case "auth/invalid-email":
        emailRef.current?.focus();
        setError("Please enter a valid email address");
        break;
      case "auth/missing-password":
        passwordRef.current?.focus();
        setError("Please enter a valid password");
        break;
      case "auth/wrong-password":
        passwordRef.current?.focus();
        setError("Invalid email/password combination");
        break;
      case "auth/too-many-requests":
        setError("Too many requests, please try again later");
        break;
      case "auth/weak-password":
        setError("The password you entered is too weak!");
        break;
      case "auth/email-already-in-use":
        setError("That Email is already in use!");
        break;
      default:
        setError("An error has occured");
        break;
    }
  };

  const validatePassword = (
    password: string
  ): { isValid: boolean; message: string } => {
    if (password.length < 8) {
      return {
        isValid: false,
        message: "Password must be at least 8 characters long",
      };
    }
    if (!/[A-Z]/.test(password)) {
      return {
        isValid: false,
        message: "Password must contain at least one uppercase letter",
      };
    }
    if (!/[a-z]/.test(password)) {
      return {
        isValid: false,
        message: "Password must contain at least one lowercase letter",
      };
    }
    if (!/[0-9]/.test(password)) {
      return {
        isValid: false,
        message: "Password must contain at least one number",
      };
    }
    if (!/[!@#$%^&*]/.test(password)) {
      return {
        isValid: false,
        message:
          "Password must contain at least one special character (!@#$%^&*)",
      };
    }
    return { isValid: true, message: "" };
  };

  return (
    <div className="auth">
      {loading && (
        <div className="auth__loading">
          <LoadingSpinner />
        </div>
      )}
      <form onSubmit={signUp} className="auth__form">
        <h1 className="auth__form__header">Register</h1>
        <input
          type="email"
          placeholder="Enter your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="auth__form__input"
        />

        <input
          type="password"
          placeholder="Enter your Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth__form__input"
        />

        {error && <p className="auth__form__error">{error}</p>}
        {success && <p className="auth__form__success">{success}</p>}

        <div className="auth__form__buttonGroup">
          <button type="submit" className="auth__form__button">
            <IoMdAdd className="bttn-icon" />
            Create Account
          </button>
          <GoogleButton
            onClick={signInWithGoogle}
            className="auth__form__google"
          />
        </div>

        <p className="auth__form__disclaimer">
          By signing in, you agree to our Terms and Conditions & Privacy Policy
        </p>
        <div className="auth__form__links">
          <button
            className="auth__form__forgotPass"
            onClick={() => navigate("/forgot-password")}
          >
            Forgot Password
          </button>
          <button
            className="auth__form__forgotPass"
            onClick={() => navigate("/signin")}
          >
            Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

export default SignUp;
